import React, { useEffect, useState } from 'react';
import DataTableHeader from '../../../components/DataTableHeader';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import Badge from '../../../components/Badge';
import DropdownButton from '../../../components/DropdownButton';
import moment from 'moment';
import ProtectiveMeasureModal from './ProtectiveMeasureModal';

export default function ProtectiveMeasuresTab({ setProtectiveMeasures, protectiveMeasures }: {
    setProtectiveMeasures: (value: ProtectiveMeasure[]) => void;
    protectiveMeasures: ProtectiveMeasure[];
}) {
    const [textFilter, setTextFilter] = useState('');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedProtectiveMeasure, setSelectedProtectiveMeasure] = useState<ProtectiveMeasure>();
    const [filteredProtectiveMeasures, setFilteredProtectiveMeasures] = useState<ProtectiveMeasure[]>([]);

    useEffect(() => {
        const filteredProtectiveMeasures = protectiveMeasures.filter(pm => pm.name.toUpperCase().includes(textFilter.toUpperCase()));
        setFilteredProtectiveMeasures(filteredProtectiveMeasures);
    }, [protectiveMeasures]);

    function getBlankProtectiveMeasure(): ProtectiveMeasure {
        return {
            id: undefined,
            aggressorElectronicMonitoring: null,
            aggressorImages: [],
            aggressorNotified: null,
            aggressorProvisionalRelease: null,
            attemptedFemicide: null,
            blobStorageId: null,
            caseNumber: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            deletedAt: null,
            document: '',
            firearmUsed: null,
            guardianWomanId: -1,
            hospitalizationDueToIncident: null,
            integrationId: null,
            name: '',
            observation: '',
            occurrenceDate: null,
            policeReportUrl: '',
            prosecutorOffice: '',
            severeInjury: null,
            victimPregnant: null,
        };
    }

    function onSave(protectiveMeasure: ProtectiveMeasure) {

        const copy = [...protectiveMeasures];
        if (!protectiveMeasure.id) {
            protectiveMeasure.id = -Math.random();
            copy.push(protectiveMeasure);
        } else {
            const index = copy.findIndex(pm => pm.id === protectiveMeasure.id);
            copy[index] = { ...copy[index], ...protectiveMeasure };
        }

        setProtectiveMeasures(copy);

    }

    useEffect(() => {
        if (!isModalVisible) {
            setSelectedProtectiveMeasure(getBlankProtectiveMeasure());
        }
    }, [isModalVisible]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            const filteredProtectiveMeasures = protectiveMeasures.filter(pm => pm.name.toUpperCase().includes(textFilter.toUpperCase()));
            setFilteredProtectiveMeasures(filteredProtectiveMeasures);
        }
    }

    return <>
        <DataTableHeader
            title={translate('registeredProtectiveMeasures')}
            zIndex={2}
            filterInput={<FilterInput
                value={textFilter ?? ''}
                onChange={text => {
                    setTextFilter(text);
                }}
                placeholder={translate('typeToSearch')}
                onKeyPress={handleKeyDown}
            />}
            actions={<CustomButton
                text={translate('register')}
                onPress={() => {
                    setModalVisible(true);
                    setSelectedProtectiveMeasure(getBlankProtectiveMeasure());
                }}
                type='secondary'
            />}
        />
        <DataTableContainer
            dataTable={<MyDataTable
                columns={[{
                    name: translate('status'),
                    cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                        <Badge
                            mode='SQUARE'
                            color={row.deletedAt === null ? (row.id! > 0 ? 'green' : 'orange') : 'red'}
                        >{translate(row.deletedAt === null ? (row.id! > 0 ? 'active_f' : 'pending') : 'revoked_f')}</Badge>
                    </View>,
                    maxWidth: '130px',
                    minWidth: '130px',
                },
                {
                    name: translate('aggressorName'),
                    selector: row => row.name ?? '',
                    wrap: true,
                    maxWidth: '400px',
                },
                {
                    name: translate('createdAt'),
                    selector: row => moment(row.createdAt).format('DD/MM/YYYY'),
                    maxWidth: '130px',
                    minWidth: '130px',
                },
                {
                    name: '',
                    cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                        {row.integrationId && <Badge
                            mode='SQUARE'
                            color={'teal'}
                        >{translate('integration')}</Badge>}
                    </View>,
                },
                {
                    button: true,
                    cell: row =>
                        <DropdownButton
                            size={200}
                            actions={[{
                                content: translate(row.integrationId || row.deletedAt ? 'checkInformation' : 'editInformation'),
                                onPress: () => {
                                    setModalVisible(true);
                                    setSelectedProtectiveMeasure(row);
                                },
                            }, {
                                content: translate('revokeProtectiveMeasure'),
                                variant: 'danger',
                                onPress: () => {
                                    // TODO: adicionar uma alertModal pra confirmar a revogacao
                                    const copy = [...protectiveMeasures];

                                    const index = copy.findIndex(pm => pm.id === row.id);
                                    copy[index] = { ...copy[index], deletedAt: new Date().toISOString() };

                                    setProtectiveMeasures(copy);
                                },
                                omit: Boolean(row.integrationId) || row.id! < 0 || Boolean(row.deletedAt),
                            }, {
                                content: translate('delete'),
                                variant: 'danger',
                                onPress: () => {
                                    // TODO: adicionar uma alertModal pra confirmar a remocao
                                    const copy = [...protectiveMeasures];

                                    const index = copy.findIndex(pm => pm.id === row.id);

                                    copy.splice(index, 1);

                                    setProtectiveMeasures(copy);
                                },
                                omit: Boolean(row.integrationId) || row.id! > 0,
                            }]}
                        />
                }]}
                data={filteredProtectiveMeasures || []}
            />}
        />
        <ProtectiveMeasureModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            protectiveMeasure={selectedProtectiveMeasure}
            onSave={onSave}
        />
    </>;
}
